import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  User, Mail, Calendar, Crown, Sparkles, ArrowRight,
  Loader2, AlertTriangle, CheckCircle2, Edit2, Save, X, Camera, Trash2, Bell
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProfilePage = () => {
  const { user, membershipTier, isFoundingMember, isVip, getAuthHeader, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || ''
  });
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = useRef(null);
  
  // Notification preferences state
  const [notifPrefs, setNotifPrefs] = useState({
    all_notifications: true,
    admin_posts: true,
    mentions_only: false,
    email_notifications: true
  });
  const [savingNotifPrefs, setSavingNotifPrefs] = useState(false);

  useEffect(() => {
    fetchNotificationPrefs();
  }, []);

  const fetchNotificationPrefs = async () => {
    try {
      const response = await axios.get(`${API_URL}/user/notification-preferences`, {
        headers: getAuthHeader()
      });
      setNotifPrefs(response.data);
    } catch (error) {
      console.error('Failed to fetch notification preferences:', error);
    }
  };

  const updateNotificationPrefs = async (newPrefs) => {
    setSavingNotifPrefs(true);
    try {
      await axios.put(`${API_URL}/user/notification-preferences`, newPrefs, {
        headers: getAuthHeader()
      });
      setNotifPrefs(newPrefs);
      toast.success('Notification preferences updated');
    } catch (error) {
      toast.error('Failed to update preferences');
    } finally {
      setSavingNotifPrefs(false);
    }
  };

  const handleNotifPrefChange = (key, value) => {
    const newPrefs = { ...notifPrefs, [key]: value };
    
    // If turning off all notifications, also turn off admin_posts
    if (key === 'all_notifications' && !value) {
      newPrefs.admin_posts = false;
    }
    // If turning on admin_posts, make sure all_notifications is on
    if (key === 'admin_posts' && value) {
      newPrefs.all_notifications = true;
    }
    // If enabling mentions_only, we can turn off admin_posts
    if (key === 'mentions_only' && value) {
      newPrefs.admin_posts = false;
    }
    
    updateNotificationPrefs(newPrefs);
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a JPG, PNG, GIF, or WebP image');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingAvatar(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${API_URL}/auth/avatar`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });
      await refreshUser();
      toast.success('Profile photo updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingAvatar(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteAvatar = async () => {
    if (!user?.avatar_url) return;
    
    setUploadingAvatar(true);
    try {
      await axios.delete(`${API_URL}/auth/avatar`, {
        headers: getAuthHeader()
      });
      await refreshUser();
      toast.success('Profile photo removed');
    } catch (error) {
      toast.error('Failed to remove photo');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const getAvatarUrl = () => {
    if (!user?.avatar_url) return null;
    // If it's a relative URL, prepend the backend URL
    if (user.avatar_url.startsWith('/api/uploads/') || user.avatar_url.startsWith('/uploads/')) {
      return `${process.env.REACT_APP_BACKEND_URL}${user.avatar_url}`;
    }
    return user.avatar_url;
  };

  const getTierInfo = () => {
    if (isVip) {
      return {
        name: 'VIP Member',
        color: 'from-amber-500 to-orange-500',
        bgColor: 'from-amber-500/10 to-orange-500/10',
        icon: Crown,
        price: '$31/month',
        description: 'Full access to everything including live classes and video library'
      };
    }
    if (isFoundingMember) {
      return {
        name: 'Founding Member',
        color: 'from-purple-500 to-pink-500',
        bgColor: 'from-purple-500/10 to-pink-500/10',
        icon: Sparkles,
        price: '$11/month',
        description: 'Live sessions, premium community, and priority support'
      };
    }
    return {
      name: 'Free Member',
      color: 'from-stone-400 to-stone-500',
      bgColor: 'from-stone-100 to-stone-200',
      icon: User,
      price: 'Free',
      description: 'Access to ebook, journals, meditations, and community'
    };
  };

  const tierInfo = getTierInfo();
  const TierIcon = tierInfo.icon;

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/auth/profile`,
        editData,
        { headers: getAuthHeader() }
      );
      await refreshUser();
      setEditing(false);
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelMembership = async () => {
    setCancelLoading(true);
    try {
      await axios.post(
        `${API_URL}/membership/cancel`,
        {},
        { headers: getAuthHeader() }
      );
      await refreshUser();
      setShowCancelModal(false);
      toast.success('Your membership has been cancelled. You\'ll retain access until the end of your billing period.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel membership');
    } finally {
      setCancelLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="profile-page">
      <div>
        <h1 className="text-3xl font-bold text-stone-900 mb-2">My Profile</h1>
        <p className="text-stone-600">Manage your account and membership</p>
      </div>

      {/* Profile Card */}
      <div className="card-cosmic p-6">
        <div className="flex items-start justify-between mb-6">
          <h2 className="text-lg font-semibold text-stone-900 flex items-center gap-2">
            <User className="w-5 h-5 text-primary" />
            Personal Information
          </h2>
          {!editing ? (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => {
                setEditData({
                  first_name: user?.first_name || '',
                  last_name: user?.last_name || ''
                });
                setEditing(true);
              }}
            >
              <Edit2 className="w-4 h-4 mr-2" />
              Edit
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setEditing(false)}
              >
                <X className="w-4 h-4 mr-1" />
                Cancel
              </Button>
              <Button 
                size="sm"
                className="btn-cosmic"
                onClick={handleSaveProfile}
                disabled={saving}
              >
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                Save
              </Button>
            </div>
          )}
        </div>

        {/* Avatar Section */}
        <div className="flex items-center gap-6 mb-6 pb-6 border-b border-stone-200">
          <div className="relative group">
            <div className="w-24 h-24 rounded-full overflow-hidden bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
              {getAvatarUrl() ? (
                <img 
                  src={getAvatarUrl()} 
                  alt="Profile" 
                  className="w-full h-full object-cover"
                />
              ) : (
                <User className="w-10 h-10 text-primary/50" />
              )}
            </div>
            {uploadingAvatar && (
              <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-white animate-spin" />
              </div>
            )}
          </div>
          <div className="flex-1">
            <p className="font-medium text-stone-900 mb-1">Profile Photo</p>
            <p className="text-sm text-stone-500 mb-3">JPG, PNG, GIF or WebP. Max 5MB.</p>
            <div className="flex gap-2">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleAvatarUpload}
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingAvatar}
              >
                <Camera className="w-4 h-4 mr-2" />
                {user?.avatar_url ? 'Change Photo' : 'Upload Photo'}
              </Button>
              {user?.avatar_url && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDeleteAvatar}
                  disabled={uploadingAvatar}
                  className="text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Remove
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {editing ? (
            <>
              <div className="space-y-2">
                <Label>First Name</Label>
                <Input
                  value={editData.first_name}
                  onChange={(e) => setEditData({...editData, first_name: e.target.value})}
                  className="input-cosmic"
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name</Label>
                <Input
                  value={editData.last_name}
                  onChange={(e) => setEditData({...editData, last_name: e.target.value})}
                  className="input-cosmic"
                />
              </div>
            </>
          ) : (
            <>
              <div>
                <p className="text-sm text-stone-500 mb-1">Name</p>
                <p className="font-medium text-stone-900">
                  {user?.first_name && user?.last_name 
                    ? `${user.first_name} ${user.last_name}` 
                    : user?.name || 'Not set'}
                </p>
              </div>
            </>
          )}
          
          <div>
            <p className="text-sm text-stone-500 mb-1 flex items-center gap-1">
              <Mail className="w-3 h-3" /> Email
            </p>
            <p className="font-medium text-stone-900">{user?.email}</p>
          </div>
          
          <div>
            <p className="text-sm text-stone-500 mb-1 flex items-center gap-1">
              <Calendar className="w-3 h-3" /> Member Since
            </p>
            <p className="font-medium text-stone-900">{formatDate(user?.created_at)}</p>
          </div>
        </div>
      </div>

      {/* Membership Card */}
      <div className={`card-cosmic p-6 bg-gradient-to-br ${tierInfo.bgColor}`}>
        <div className="flex items-start justify-between mb-6">
          <h2 className="text-lg font-semibold text-stone-900 flex items-center gap-2">
            <Crown className="w-5 h-5 text-primary" />
            Membership
          </h2>
        </div>

        <div className="flex flex-col md:flex-row md:items-center gap-6">
          <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${tierInfo.color} flex items-center justify-center flex-shrink-0`}>
            <TierIcon className="w-8 h-8 text-white" />
          </div>
          
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-1">
              <h3 className="text-xl font-bold text-stone-900">{tierInfo.name}</h3>
              <span className={`text-sm font-semibold px-3 py-1 rounded-full bg-gradient-to-r ${tierInfo.color} text-white`}>
                {tierInfo.price}
              </span>
            </div>
            <p className="text-stone-600">{tierInfo.description}</p>
            
            {user?.premium_since && (
              <p className="text-sm text-stone-500 mt-2">
                Premium member since {formatDate(user.premium_since)}
              </p>
            )}
          </div>
        </div>

        {/* Upgrade or Cancel Options */}
        <div className="mt-6 pt-6 border-t border-stone-200 flex flex-wrap gap-3">
          {!isVip && (
            <Button
              onClick={() => navigate('/pricing')}
              className={isFoundingMember 
                ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white'
                : 'btn-cosmic'
              }
            >
              {isFoundingMember ? 'Upgrade to VIP' : 'Upgrade Membership'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
          
          {(isFoundingMember || isVip) && (
            <Button
              variant="outline"
              className="text-red-600 border-red-200 hover:bg-red-50"
              onClick={() => setShowCancelModal(true)}
            >
              Cancel Membership
            </Button>
          )}
        </div>
      </div>

      {/* Notification Preferences */}
      <div className="card-cosmic p-6">
        <h2 className="text-lg font-semibold text-stone-900 mb-4 flex items-center gap-2">
          <Bell className="w-5 h-5 text-primary" />
          Notification Preferences
        </h2>
        
        <div className="space-y-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={notifPrefs.all_notifications}
              onChange={(e) => handleNotifPrefChange('all_notifications', e.target.checked)}
              disabled={savingNotifPrefs}
              className="w-5 h-5 rounded border-stone-300 text-primary focus:ring-primary mt-0.5"
            />
            <div>
              <span className="font-medium text-stone-900">Enable all notifications</span>
              <p className="text-sm text-stone-500">Receive notifications for posts from Heather and when you're mentioned</p>
            </div>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={notifPrefs.admin_posts}
              onChange={(e) => handleNotifPrefChange('admin_posts', e.target.checked)}
              disabled={savingNotifPrefs || !notifPrefs.all_notifications}
              className="w-5 h-5 rounded border-stone-300 text-primary focus:ring-primary mt-0.5"
            />
            <div>
              <span className={`font-medium ${notifPrefs.all_notifications ? 'text-stone-900' : 'text-stone-400'}`}>
                Posts from Heather
              </span>
              <p className="text-sm text-stone-500">Get notified when Heather posts in your community</p>
            </div>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={notifPrefs.mentions_only}
              onChange={(e) => handleNotifPrefChange('mentions_only', e.target.checked)}
              disabled={savingNotifPrefs}
              className="w-5 h-5 rounded border-stone-300 text-primary focus:ring-primary mt-0.5"
            />
            <div>
              <span className="font-medium text-stone-900">Mentions only</span>
              <p className="text-sm text-stone-500">Only notify me when someone @mentions me directly</p>
            </div>
          </label>

          <div className="border-t border-stone-200 pt-4 mt-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={notifPrefs.email_notifications}
                onChange={(e) => handleNotifPrefChange('email_notifications', e.target.checked)}
                disabled={savingNotifPrefs}
                className="w-5 h-5 rounded border-stone-300 text-primary focus:ring-primary mt-0.5"
              />
              <div>
                <span className="font-medium text-stone-900">Email notifications</span>
                <p className="text-sm text-stone-500">Also send notifications to your email ({user?.email})</p>
              </div>
            </label>
          </div>

          <div className="border-t border-stone-200 pt-4 mt-4">
            <h3 className="font-medium text-stone-900 mb-3">Private Messages</h3>
            <div className="space-y-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={notifPrefs.message_notifications !== false}
                  onChange={(e) => handleNotifPrefChange('message_notifications', e.target.checked)}
                  disabled={savingNotifPrefs}
                  className="w-5 h-5 rounded border-stone-300 text-primary focus:ring-primary mt-0.5"
                />
                <div>
                  <span className="font-medium text-stone-900">Message notifications</span>
                  <p className="text-sm text-stone-500">Get notified when you receive a private message</p>
                </div>
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={notifPrefs.message_email_notifications !== false}
                  onChange={(e) => handleNotifPrefChange('message_email_notifications', e.target.checked)}
                  disabled={savingNotifPrefs}
                  className="w-5 h-5 rounded border-stone-300 text-primary focus:ring-primary mt-0.5"
                />
                <div>
                  <span className="font-medium text-stone-900">Message email notifications</span>
                  <p className="text-sm text-stone-500">Also receive message notifications via email</p>
                </div>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Membership Benefits */}
      <div className="card-cosmic p-6">
        <h2 className="text-lg font-semibold text-stone-900 mb-4">Your Benefits</h2>
        <div className="grid md:grid-cols-2 gap-3">
          {[
            { name: 'Ebook Access', available: true },
            { name: 'Downloadable Journals', available: true },
            { name: 'Meditation Library', available: true },
            { name: 'Podcast Episodes', available: true },
            { name: 'Free Community Chat', available: true },
            { name: 'Movement Classes', available: true },
            { name: 'Live Sessions', available: isFoundingMember || isVip },
            { name: 'Private Founding Chat', available: isFoundingMember || isVip },
            { name: 'Live Movement Classes', available: isVip },
            { name: 'Full Video Library', available: isVip },
            { name: 'Private VIP Chat', available: isVip },
          ].map((benefit, i) => (
            <div 
              key={i} 
              className={`flex items-center gap-2 p-2 rounded-lg ${benefit.available ? 'text-stone-900' : 'text-stone-400'}`}
            >
              <CheckCircle2 className={`w-5 h-5 ${benefit.available ? 'text-secondary' : 'text-stone-300'}`} />
              <span className={benefit.available ? '' : 'line-through'}>{benefit.name}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card-cosmic p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-stone-900">Cancel Membership?</h3>
                <p className="text-sm text-stone-500">This action can't be undone</p>
              </div>
            </div>
            
            <p className="text-stone-600 mb-6">
              Are you sure you want to cancel your {tierInfo.name} membership? 
              You'll lose access to premium features at the end of your current billing period.
            </p>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowCancelModal(false)}
              >
                Keep Membership
              </Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                onClick={handleCancelMembership}
                disabled={cancelLoading}
              >
                {cancelLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Yes, Cancel'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
